import React, { ChangeEvent, Dispatch, DragEvent, SetStateAction, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Stack, TextField, Typography } from "@mui/material";
import { theme } from "src/theme/mui-theme";
import { ts } from "src/utils/translate";
import FolderIcon from "src/pages/ProjectDetailsWizard/images/folder_icon.jpg"
import SampleFullLayout from "src/pages/ProjectDetailsWizard/images/sample_full_layout.jpg";
import { useGetFloorsQuery } from "src/api/v2/floor-v2-ApiSlice";
import { getFrontendURL } from "src/utils/url";
import { FloorFormData, FloorLayout } from "src/pages/PlannerPage/FloorWizard/useUpsertFloor";
import { closePdf, getPdfPageImage, openPdf, PdfDocument } from "src/pages/PlannerPage/FloorWizard/pdfPageRenderer";
import { useAppDispatch } from "src/app/hooks";
import { showSnackBar } from "src/api/ui-slice";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const PDF_MIME_TYPE = "application/pdf";
const PDF_PAGE_RENDER_DEBOUNCE_MS = 300;
const MAX_IMAGE_SIZE_BYTES = 20 * 1024 * 1024;

interface UploadLayoutStepProps {
    projectId: number;
    floorId?: number;
    formData: FloorFormData;
    setFormData: Dispatch<SetStateAction<FloorFormData>>;
}

export const FloorWizardUploadLayoutStep = ({ projectId, floorId, formData, setFormData }: UploadLayoutStepProps) => {

    const dispatch = useAppDispatch();

    const { data: floors, isLoading } = useGetFloorsQuery({ project_id: projectId }, {
        skip: !projectId,
    });

    const handleChange = (field: keyof FloorFormData) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [field]: event.target.value });
    };

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    
    const [pdfFileOnMount] = useState(formData.planPdfFile);

    const [pdfPageCount, setPdfPageCount] = useState(0);
    const [pdfPageInput, setPdfPageInput] = useState(String(formData.planPdfPage));
    const [isPdfRendering, setIsPdfRendering] = useState(false);
    const pdfRef = useRef<PdfDocument | null>(null);
    const pdfRenderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pdfRequestIdRef = useRef(0);

    useEffect(() => {
        if (!isLoading && floors?.floors && floorId != null) {

            const selected = floors?.floors.find(
                (floor) => floor.id === floorId
            );

            if (selected?.plan_image) {

                setFormData((prev: FloorFormData) => ({
                    ...prev,
                    floorId: floorId,
                    floorName: selected?.floor_name,
                    ceilingHeight: String(selected.headroom),
                    planPreviewImageUrl: `${getFrontendURL()}/images/floor_images/${selected?.plan_image}`
                }));
            }
        }
    }, [isLoading, floors, setFormData, floorId]);

    useEffect(() => {
        if (!pdfFileOnMount) {
            return;
        }

        const requestId = pdfRequestIdRef.current;
        openPdf(pdfFileOnMount)
            .then((pdf) => {
                if (requestId !== pdfRequestIdRef.current) {
                    closePdf(pdf);
                    return;
                }
                pdfRef.current = pdf;
                setPdfPageCount(pdf.pageCount);
            })
            .catch(() => {
                setFormData((prev: FloorFormData) => ({ ...prev, planPdfFile: null, planPdfPage: 1 }));
                dispatch(showSnackBar({ text: ts('pdf_load_error', 'Error with pdf file'), severity: 'error' }));
            });
    }, [pdfFileOnMount, setFormData, dispatch]);

    useEffect(() => {
        setPdfPageInput(String(formData.planPdfPage));
    }, [formData.planPdfPage]);

    // Held in a ref so the paste listener stays attached across renders.
    const applySelectedFileRef = useRef<(file?: File) => void>(() => undefined);

    useEffect(() => {
        applySelectedFileRef.current = applySelectedFile;
    });

    useEffect(() => {
        const handlePaste = (event: ClipboardEvent) => {
            const pastedImage = Array.from(event.clipboardData?.items ?? [])
                .find((item) => item.kind === "file" && item.type.startsWith("image/"))
                ?.getAsFile();

            if (pastedImage) {
                applySelectedFileRef.current(pastedImage);
            }
        };

        document.addEventListener("paste", handlePaste);
        return () => document.removeEventListener("paste", handlePaste);
    }, []);

    useEffect(() => () => {
        pdfRequestIdRef.current++;
        if (pdfRenderTimerRef.current) {
            clearTimeout(pdfRenderTimerRef.current);
        }
        if (pdfRef.current) {
            closePdf(pdfRef.current);
            pdfRef.current = null;
        }
    }, []);

    const handleSelectFileClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        handleBoxClick();
    };

    const handleBoxClick = () => {
        if (isPdfRendering) {
            return;
        }
        const input = fileInputRef.current;
        if (input) {
            input.value = "";
            input.click();
        }
    };

    const handleRemoveClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        handleRemoveImage();
    };

    const handleRemoveImage = () => {
        resetPdfState();
        setFormData((prev: FloorFormData) => ({
            ...prev,
            planImageFile: null,
            planPreviewImageUrl: null,
            planPdfFile: null,
            planPdfPage: 1
        }));
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        applySelectedFile(event.target.files?.[0]);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        applySelectedFile(event.dataTransfer.files?.[0]);
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    const applySelectedFile = (file?: File) => {
        if (!file || isPdfRendering || formData.selectedLayout !== FloorLayout.UPLOAD) {
            return;
        }

        if (file.type === PDF_MIME_TYPE) {
            void applySelectedPdf(file);
            return;
        }

        if (!file.type.startsWith("image/")) {
            dispatch(showSnackBar({ text: ts('unsupported_layout_file', 'Please upload an image or a PDF file'), severity: 'error' }));
            return;
        }

        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            dispatch(showSnackBar({ text: ts('p_file_too_big_20', 'File is too big (Max 20MiB)'), severity: 'error' }));
            return;
        }

        resetPdfState();
        setFormData((prev: FloorFormData) => ({ ...prev, planPdfFile: null, planPdfPage: 1 }));
        applyPlanImage(file);
    };

    const applySelectedPdf = async (file: File) => {
        resetPdfState();
        const requestId = pdfRequestIdRef.current;
        setIsPdfRendering(true);
        setFormData((prev: FloorFormData) => ({
            ...prev,
            planImageFile: null,
            planPreviewImageUrl: null,
            planPdfFile: file,
            planPdfPage: 1
        }));

        try {
            const pdf = await openPdf(file);
            if (requestId !== pdfRequestIdRef.current) {
                closePdf(pdf);
                return;
            }
            pdfRef.current = pdf;
            setPdfPageCount(pdf.pageCount);
            await renderPdfPage(1);
        } catch (error) {
            handlePdfError(error);
        }
    };

    const handlePreviousPdfPage = () => goToPdfPage(formData.planPdfPage - 1);

    const handleNextPdfPage = () => goToPdfPage(formData.planPdfPage + 1);

    const handlePdfPageInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const typedPage = event.target.value;
        setPdfPageInput(typedPage);

        const pageNumber = Number(typedPage);
        if (Number.isInteger(pageNumber) && pageNumber >= 1 && pageNumber <= pdfPageCount) {
            goToPdfPage(pageNumber);
        }
    };

    const handlePdfPageInputKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            commitPdfPageInput();
        }
    };

    // Whatever the field is left with gets clamped into range, so it can't stay out of sync with the preview.
    const commitPdfPageInput = () => {
        const typedPage = Number(pdfPageInput);
        const pageNumber = Number.isInteger(typedPage) ? clampPdfPage(typedPage) : formData.planPdfPage;
        goToPdfPage(pageNumber);
        setPdfPageInput(String(pageNumber));
    };

    const clampPdfPage = (page: number) => Math.min(Math.max(page, 1), pdfPageCount);

    const goToPdfPage = (page: number) => {
        const pageNumber = clampPdfPage(page);
        if (pageNumber === formData.planPdfPage) {
            return;
        }

        pdfRequestIdRef.current++;
        setIsPdfRendering(true);
        setFormData((prev: FloorFormData) => ({
            ...prev,
            planImageFile: null,
            planPreviewImageUrl: null,
            planPdfPage: pageNumber
        }));

        if (pdfRenderTimerRef.current) {
            clearTimeout(pdfRenderTimerRef.current);
        }
        pdfRenderTimerRef.current = setTimeout(() => void renderPdfPage(pageNumber), PDF_PAGE_RENDER_DEBOUNCE_MS);
    };

    const renderPdfPage = async (pageNumber: number) => {
        const pdf = pdfRef.current;
        if (!pdf) {
            setIsPdfRendering(false);
            return;
        }

        const requestId = ++pdfRequestIdRef.current;
        setIsPdfRendering(true);

        try {
            const pageImage = await getPdfPageImage(pdf, pageNumber);

            if (requestId !== pdfRequestIdRef.current) {
                return;
            }

            applyPlanImage(pageImage);
            setIsPdfRendering(false);
        } catch (error) {
            if (requestId === pdfRequestIdRef.current) {
                handlePdfError(error);
            }
        }
    };

    const handlePdfError = (error: unknown) => {
        console.warn("Could not render the uploaded PDF", error);
        resetPdfState();
        setFormData((prev: FloorFormData) => ({
            ...prev,
            planImageFile: null,
            planPreviewImageUrl: null,
            planPdfFile: null,
            planPdfPage: 1
        }));
        dispatch(showSnackBar({ text: ts('pdf_load_error', 'Error with pdf file'), severity: 'error' }));
    };

    const resetPdfState = () => {
        if (pdfRenderTimerRef.current) {
            clearTimeout(pdfRenderTimerRef.current);
            pdfRenderTimerRef.current = null;
        }
        pdfRequestIdRef.current++;
        if (pdfRef.current) {
            closePdf(pdfRef.current);
            pdfRef.current = null;
        }
        setPdfPageCount(0);
        setIsPdfRendering(false);
    };

    const applyPlanImage = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            setFormData((prev: FloorFormData) => ({
                ...prev,
                planPreviewImageUrl: e.target?.result as string
            }));
        };
        reader.readAsDataURL(file);

        setFormData((prev: FloorFormData) => ({
            ...prev,
            planImageFile: file
        }));
    };

    return (
        <>
            <Box marginY={2}>
                <Box
                    onClick={handleBoxClick}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    sx={{
                        width: "100%",
                        height: 300,
                        border: `2px dashed ${theme.palette.primary.main}`,
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "transparent",
                        cursor: isPdfRendering ? "default" : "pointer",
                        position: "relative",
                    }}
                >
                    { formData.selectedLayout === FloorLayout.UPLOAD ? (
                        <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,.pdf,.svg"
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                        />
                        { isPdfRendering ? (
                                <Stack alignItems="center" spacing={2}>
                                    <CircularProgress />
                                    <Typography variant="subtitle2" color={theme.palette.text.primary}>
                                        {ts('loading_pdf', 'Loading PDF, please wait...')}
                                    </Typography>
                                </Stack>
                            ) : formData.planPreviewImageUrl ? (
                                <>
                                    <IconButton
                                        onClick={handleRemoveClick}
                                        size="small"
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            backgroundColor: "transparent",
                                            color: theme.palette.primary.main,
                                            "&:hover": {
                                                backgroundColor: "transparent",
                                            },
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                    <img
                                        src={formData.planPreviewImageUrl}
                                        alt="Preview"
                                        style={{
                                            width: "90%",
                                            height: "90%",
                                            objectFit: "contain",
                                            pointerEvents: "none",
                                            userSelect: "none"
                                        }}
                                        onError={() => {
                                            console.warn("Floor plan image could not be loaded, clearing the selection.");
                                            handleRemoveImage();
                                        }}
                                    />
                                </>
                            ) : (
                                <>
                                    <img
                                        src={FolderIcon}
                                        alt="Upload File"
                                        style={{
                                            width: 70,
                                            height: 70,
                                            objectFit: "cover",
                                            paddingBottom: 10
                                        }}
                                    />

                                    <Typography variant="subtitle2" color={theme.palette.text.primary}>
                                        {ts('drag_file_here_to_upload_layout', 'Drag your file here to upload layout')} (jpg, png, pdf etc.)
                                    </Typography>

                                    <Typography variant="caption" color={theme.palette.text.secondary} marginTop={1}>
                                        {ts('or_word', 'or')}
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        sx={{ width: 120, marginTop: 2 }}
                                        onClick={handleSelectFileClick}
                                    >
                                        {ts('select_file', 'Select file')}
                                    </Button>

                                    <Typography variant="caption" color={theme.palette.text.secondary} marginTop={3}>
                                        ({ts('can_take_screen_and_paste', 'You can take a screenshot and paste it using Ctrl+V.')})
                                    </Typography>
                                </>
                            )
                        }</>
                    ) : (
                        <>
                            <img
                                src={SampleFullLayout}
                                alt="Sample Layout"
                                style={{
                                    width: "90%",
                                    height: "90%",
                                    objectFit: "contain"
                                }}
                            />
                        </>
                    )}
                </Box>

                {pdfPageCount > 1 && (
                    <Stack direction="row" alignItems="center" spacing={0.5} marginTop={1}>
                        <IconButton
                            size="small"
                            onClick={handlePreviousPdfPage}
                            disabled={formData.planPdfPage <= 1}
                        >
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>

                        <TextField
                            variant="outlined"
                            size="small"
                            type="number"
                            value={pdfPageInput}
                            onChange={handlePdfPageInputChange}
                            onBlur={commitPdfPageInput}
                            onKeyDown={handlePdfPageInputKeyDown}
                            sx={{
                                width: 52,
                                "& input": {
                                    textAlign: "center",
                                    fontSize: 14,
                                    padding: "4px 2px",
                                    MozAppearance: "textfield",
                                    appearance: "textfield",
                                },
                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                    display: "none",
                                },
                            }}
                            slotProps={{
                                htmlInput: {
                                    min: 1,
                                    max: pdfPageCount,
                                    inputMode: "numeric",
                                    "aria-label": ts('pdf_page_select_hint', 'Select page to use as layout'),
                                },
                            }}
                        />

                        <Typography variant="body2" color={theme.palette.text.secondary}>
                            {`/ ${pdfPageCount}`}
                        </Typography>

                        <IconButton
                            size="small"
                            onClick={handleNextPdfPage}
                            disabled={formData.planPdfPage >= pdfPageCount}
                        >
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>

                        <Typography variant="caption" color={theme.palette.text.secondary} paddingLeft={1}>
                            {ts('pdf_page_select_hint', 'Select page to use as layout')}
                        </Typography>
                    </Stack>
                )}
            </Box>

            <Stack direction="row" justifyContent="space-between" marginTop={3}>
                <TextField
                    label={ts('floor_name', 'Floor name')}
                    variant="outlined"
                    value={formData.floorName || ""}
                    onChange={handleChange("floorName")}
                    placeholder={ts('floor_name_default', 'Floor')}
                    size="small"
                    sx={{ width: 300 }}
                    slotProps={{ inputLabel: { shrink: true } }}
                />

                <TextField
                    label={ts('ceiling_height', 'Ceiling height')}
                    variant="outlined"
                    value={formData.ceilingHeight || ""}
                    onChange={handleChange("ceilingHeight")}
                    size="small"
                    required={true}
                    sx={{ width: 150 }}
                    slotProps={{
                        inputLabel: { shrink: true },
                        input: {
                            endAdornment: (
                                <Typography
                                    variant="body2"
                                    sx={{ color: theme.palette.text.secondary, ml: 1 }}
                                >
                                    cm
                                </Typography>
                            ),
                        },
                    }}
                />
            </Stack>
        </>
    );
};